import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope, useCurrentUser } from '../../../shared/scope/useCurrentScope.js';

import {
  acceptMyInvitation,
  createInvitation,
  listInvitations,
  listMyPendingInvitations,
  listTeamMembers,
  revokeInvitation,
  updateTeamMember,
  type CreateInvitationInput,
  type CreateInvitationResult,
  type MyPendingInvitation,
  type TeamInvitation,
  type TeamMember,
  type UpdateMemberInput,
} from '../api/team.client.js';

/** Состав команды зависит от выбранного пространства — ключ его учитывает. */
function useTeamKey(): readonly string[] {
  const scope = useCurrentScope();
  return ['team', scope?.workspaceId ?? 'anonymous'];
}

export function useTeamMembers() {
  const key = useTeamKey();
  const scope = useCurrentScope();

  return useQuery<TeamMember[]>({
    queryKey: [...key, 'members'],
    queryFn: listTeamMembers,
    enabled: scope !== null,
  });
}

export function useTeamInvitations() {
  const key = useTeamKey();
  const scope = useCurrentScope();

  return useQuery({
    queryKey: [...key, 'invitations'],
    queryFn: listInvitations,
    enabled: scope !== null,
  });
}

function useInvalidateTeam(): () => Promise<void> {
  const queryClient = useQueryClient();
  const key = useTeamKey();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    // Дропдаун «исполнитель» берёт участников отдельным запросом: отключённый
    // участник должен пропасть и оттуда.
    await queryClient.invalidateQueries({ queryKey: ['members'] });
  };
}

export function useUpdateMemberMutation() {
  const invalidate = useInvalidateTeam();

  return useMutation<TeamMember, Error, { id: string; input: UpdateMemberInput }>({
    mutationFn: ({ id, input }) => updateTeamMember(id, input),
    onSuccess: () => invalidate(),
  });
}

export function useCreateInvitationMutation() {
  const invalidate = useInvalidateTeam();

  return useMutation<CreateInvitationResult, Error, CreateInvitationInput>({
    mutationFn: (input) => createInvitation(input),
    onSuccess: () => invalidate(),
  });
}

export function useRevokeInvitationMutation() {
  const invalidate = useInvalidateTeam();

  return useMutation<void, Error, string>({
    mutationFn: (invitationId) => revokeInvitation(invitationId),
    onSuccess: () => invalidate(),
  });
}

export function useMyPendingInvitations() {
  const user = useCurrentUser();
  return useQuery<MyPendingInvitation[]>({
    queryKey: ['invitations', 'my-pending', user?.email ?? 'anon'],
    queryFn: listMyPendingInvitations,
    enabled: user !== null,
    staleTime: 30_000,
  });
}

export function useAcceptMyInvitationMutation() {
  const queryClient = useQueryClient();
  return useMutation<{ workspaceId: string }, Error, string>({
    mutationFn: (invitationId) => acceptMyInvitation(invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invitations'] });
      await queryClient.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export type { MyPendingInvitation, TeamInvitation, TeamMember };
